
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

// import NavBar from '../../../views/utils/NavBar';
import ControlPanel from './utils-components/ControlPanel';
import FormView from './utils-components/FormView';
import SelectionContext from './utils-components/SelectionContext';

// components
import Box from './primitive/Box';
import Button from './primitive/Button';
import Check from './primitive/Check';
import Drop from './primitive/Drop';
import Image from './primitive/Image';
import Input from './primitive/Input';
import Label from './primitive/Label';
import List from './primitive/List';
import Radio from './primitive/Radio';

import InputTable from './intermediate/InputTable';
import Signature from './intermediate/Signature';
import Snapshot from './intermediate/Snapshot';

// import Header from '../../../views/utils/Header';
import FormService from '../../redux/services/forms';
import Modal from './utils/Modal';

import { message } from "antd";

import { getHeight, whichLayoutForPages } from './misc/layouts';
import './create-form.css';

const info = () => {
    message.info('Use CTRL/Command+Enter to save the changes of the form.', 5);
};


// export default function CreateForm() {
const CreateForm = (props) => {
    let size_of_grid = window.innerWidth * 0.00001;
    const location = useLocation();
    const [messageApi, contextHolder] = message.useMessage();
    const [saveStatus, setSaveStatus] = useState(0); // -1 = error, 0 = nothing, 1 = success
    const [loaded, setLoaded] = useState(false);
    const [dbId, setDbId] = useState('');

    const [name, setName] = useState('My Form');

    // creationIndex is a unique number given to each element of the form
    // describing when it was created. no two elements can ever have the same creation
    // index, since it is incremented for each newly created element.
    // this variable describes the index to be given to the next newly created element.
    const [creationIndex, setCreationIndex] = useState(0);


    // Array of components to be rendered. setComponentsRaw is the plain state
    // setter; setComponents (defined below) wraps it to record undo/redo history.
    const [components, setComponentsRaw] = useState([]);

    // ID in component array of currently selected/hovered element
    const [selectedEl, setSelectedEl] = useState(-1);
    // Indices of elements captured by a marquee multi-selection (>= 2 means a
    // group is active; single-select continues to use selectedEl).
    const [multiSel, setMultiSel] = useState([]);

    // Element-to-element alignment snapping ("smart guides"). On by default.
    const [magneticSnap, setMagneticSnap] = useState(true);
    const [snapGuides, setSnapGuides] = useState([]); // active guide lines (viewport px)
    const [contextMenu, setContextMenu] = useState([false, -1, [0, 0]]); // isContextMenuShowing?, id of active el, coordinates
    const [hoveredEl, setHoveredEl] = useState(-1);
    const message_key = 'updatable';

    const [magneticGrid, setMagneticGrid] = useState(true);
    const [magneticGridPartitions, setMagneticGridPartitions] = useState(800);

    // possible layouts: None, A3, A4
    const [layout, setLayout] = useState("A4");

    // ---- Undo / redo history ----------------------------------------------
    // Snapshots are stored as JSON strings (cheap to measure + naturally deep
    // cloned). The total is capped to a byte budget so very large forms can't
    // blow up memory; the oldest entries are evicted once the cap is exceeded.
    // Bursts of changes (dragging, resizing, typing in the properties panel)
    // are coalesced into a single entry via a short trailing debounce.
    const HISTORY_BYTE_LIMIT = 30 * 1024 * 1024; // ~30 MB
    const HISTORY_DEBOUNCE_MS = 400;

    const historyRef = useRef({ stack: [], index: -1, bytes: 0 });
    const pendingRef = useRef({ value: null, timer: null });
    const componentsRef = useRef([]);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    useEffect(() => {
        componentsRef.current = components;
    }, [components]);

    // Refs mirror the current selection so the (mount-only) keyboard handler
    // always reads fresh values without re-binding on every change.
    const selRef = useRef(-1);
    const multiSelRef = useRef([]);
    const currentPageRef = useRef(0);
    const creationIndexRef = useRef(0);
    useEffect(() => { selRef.current = selectedEl; }, [selectedEl]);
    useEffect(() => { multiSelRef.current = multiSel; }, [multiSel]);
    useEffect(() => { creationIndexRef.current = creationIndex; }, [creationIndex]);

    useEffect(() => {
        const onKeyDown = (event) => {
            // Skip everything while a modal / the script editor is open.
            const overlayOpen = document.querySelector('.script-editor-root, .stpc-modal') ||
                Array.prototype.some.call(document.querySelectorAll('.ant-modal-wrap'), (w) => w.offsetParent !== null);
            if (overlayOpen) return;

            const t = event.target;
            const editable = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' ||
                t.tagName === 'SELECT' || t.isContentEditable);
            // An element's own inner field counts as "the canvas", not a panel.
            const inCanvas = !!(t && t.closest && t.closest('.form-view'));

            // Current selection (group if active, else single).
            const selectedIds = () => (multiSelRef.current && multiSelRef.current.length > 0)
                ? [...multiSelRef.current]
                : (selRef.current >= 0 ? [selRef.current] : []);

            const ctrl = event.ctrlKey || event.metaKey;

            // Copy — store selected elements in localStorage as text so they can
            // be pasted into any form (other tab / after navigation).
            if (ctrl && (event.key === 'c' || event.key === 'C')) {
                if (editable && !inCanvas) return; // typing in a panel field
                const ids = selectedIds();
                if (ids.length === 0) return;
                try {
                    localStorage.setItem('stpc-clipboard', JSON.stringify({
                        __stpcElements: true,
                        elements: ids.map((i) => componentsRef.current[i])
                    }));
                } catch (e) { /* ignore */ }
                return;
            }
            // Paste.
            if (ctrl && (event.key === 'v' || event.key === 'V')) {
                if (editable && !inCanvas) return;
                let data;
                try { data = JSON.parse(localStorage.getItem('stpc-clipboard')); } catch (e) { return; }
                if (!data || data.__stpcElements !== true || !Array.isArray(data.elements) || data.elements.length === 0) return;
                event.preventDefault();
                let new_comps = [...componentsRef.current];
                let ci = creationIndexRef.current;
                const newIdx = [];
                data.elements.forEach((el) => {
                    const copy = JSON.parse(JSON.stringify(el));
                    copy.page = currentPageRef.current;
                    copy.position = [(parseFloat(copy.position && copy.position[0]) || 0) + 0.02, (parseFloat(copy.position && copy.position[1]) || 0) + 0.02];
                    if ('creation_index' in copy && ci !== -1) {
                        ci = ci + 1;
                        copy.creation_index = ci;
                    }
                    newIdx.push(new_comps.length);
                    new_comps.push(copy);
                });
                if (ci !== creationIndexRef.current) setCreationIndex(ci);
                setComponents(new_comps);
                if (newIdx.length > 1) { setMultiSel(newIdx); setSelectedEl(-1); }
                else { setSelectedEl(newIdx[0]); setMultiSel([]); }
                return;
            }

            // Remaining shortcuts must not fire while typing in any field.
            if (editable) return;
            // Arrow-key nudge
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                const ids = selectedIds();
                if (ids.length === 0) return;
                event.preventDefault();
                const step = event.shiftKey ? 0.02 : 0.005;
                const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
                const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
                let new_comps = [...componentsRef.current];
                ids.forEach((i) => {
                    if (new_comps[i].locked) return;
                    const px = parseFloat(new_comps[i].position[0]) || 0;
                    const py = parseFloat(new_comps[i].position[1]) || 0;
                    new_comps[i] = { ...new_comps[i], position: [px + dx, py + dy] };
                });
                setComponents(new_comps);
                return;
            }

            if (event.key === 'Delete' || event.key === 'Backspace') {
                const ms = multiSelRef.current;
                if (ms && ms.length > 0) {
                    event.preventDefault();
                    let new_comps = [...componentsRef.current];
                    [...ms].sort((a, b) => b - a).forEach((i) => new_comps.splice(i, 1));
                    setComponents(new_comps);
                    setMultiSel([]);
                    setSelectedEl(-1);
                } else if (selRef.current >= 0) {
                    event.preventDefault();
                    let new_comps = [...componentsRef.current];
                    new_comps.splice(selRef.current, 1);
                    setComponents(new_comps);
                    setSelectedEl(-1);
                }
            } else if (event.key === 'Escape') {
                setMultiSel([]);
                setSelectedEl(-1);
                setContextMenu([false, -1, [0, 0]]);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshHistoryButtons = () => {
        const h = historyRef.current;
        setCanUndo(h.index > 0);
        setCanRedo(h.index < h.stack.length - 1);
    };

    const pushSnapshot = (json) => {
        const h = historyRef.current;
        // A new edit invalidates any redo branch.
        if (h.index < h.stack.length - 1) {
            const removed = h.stack.splice(h.index + 1);
            for (let i = 0; i < removed.length; i++) h.bytes -= removed[i].length;
        }
        // Ignore no-op changes that produce an identical snapshot.
        if (h.stack.length > 0 && h.stack[h.index] === json) return;
        h.stack.push(json);
        h.bytes += json.length;
        h.index = h.stack.length - 1;
        // Enforce the memory cap, always keeping enough to undo once.
        while (h.bytes > HISTORY_BYTE_LIMIT && h.stack.length > 2) {
            const evicted = h.stack.shift();
            h.bytes -= evicted.length;
            h.index -= 1;
        }
        refreshHistoryButtons();
    };

    const commitPending = () => {
        if (pendingRef.current.timer) {
            clearTimeout(pendingRef.current.timer);
            pendingRef.current.timer = null;
        }
        // Serialize only once, when the burst settles, instead of on every edit
        // tick — important for large forms during live drag / resize.
        if (pendingRef.current.value !== null) {
            pushSnapshot(JSON.stringify(pendingRef.current.value));
            pendingRef.current.value = null;
        }
    };

    const recordEdit = (prev, resolved) => {
        const h = historyRef.current;
        if (h.stack.length === 0) {
            // Seed the baseline with the pre-edit state so the first edit after
            // a fresh load / new form is itself undoable.
            const baseJson = JSON.stringify(prev);
            h.stack.push(baseJson);
            h.bytes += baseJson.length;
            h.index = 0;
        }
        pendingRef.current.value = resolved;
        if (pendingRef.current.timer) clearTimeout(pendingRef.current.timer);
        pendingRef.current.timer = setTimeout(commitPending, HISTORY_DEBOUNCE_MS);
    };

    // Tracked setter: same signature as the raw state setter, used everywhere
    // an edit happens so the change lands in the undo history.
    const setComponents = (next) => {
        const prev = componentsRef.current;
        const resolved = typeof next === 'function' ? next(prev) : next;
        recordEdit(prev, resolved);
        componentsRef.current = resolved;
        setComponentsRaw(resolved);
    };

    const undo = () => {
        commitPending();
        const h = historyRef.current;
        if (h.index <= 0) return;
        h.index -= 1;
        const restored = JSON.parse(h.stack[h.index]);
        componentsRef.current = restored;
        setComponentsRaw(restored);
        setSelectedEl(-1);
        setMultiSel([]);
        setContextMenu([false, -1, [0, 0]]);
        refreshHistoryButtons();
    };

    const redo = () => {
        commitPending();
        const h = historyRef.current;
        if (h.index >= h.stack.length - 1) return;
        h.index += 1;
        const restored = JSON.parse(h.stack[h.index]);
        componentsRef.current = restored;
        setComponentsRaw(restored);
        setSelectedEl(-1);
        setMultiSel([]);
        setContextMenu([false, -1, [0, 0]]);
        refreshHistoryButtons();
    };

    const saveForm = () => {
        let form_obj = {
            name: name,
            content: components,
            pages: pages,
            vars: vars,
            externals: {
                ...externals,
                ...(creationIndex != -1 && {creation_index: creationIndex}),
            },
            parent: parent
        };
        console.log('form_obj > ', form_obj);
        const formId = document.getElementById('formId').textContent;
        if (formId !== '') {
            messageApi.open({
                key: message_key,
                type: 'loading',
                content: 'Saving...',
            });

            FormService.updateForm(formId, form_obj).then(resp => {
                getFormPages();
                messageApi.open({
                    key: message_key,
                    type: 'success',
                    content: 'Your form was successfully saved.',
                });
            }).catch(err => {
                messageApi.open({
                    key: message_key,
                    type: 'error',
                    content: 'Your form could not be saved.',
                });
            });
        }
    }


    const saveButton = useRef();
    // handle what happens on key press
    const handleKeyPress = useCallback((event) => {

        if ((event.ctrlKey === true || event.metaKey === true) && event.key == 'Enter') {
            saveButton.current.click();
        }
    }, []);


    useEffect(() => {
        // attach the event listener
        document.addEventListener('keydown', handleKeyPress);

        // remove the event listener
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, []);


    // States for pages
    const [pages, setPages] = useState([
        {
            height: getHeight('A4', 0.7),
            visibleTo: 'all',
            editableTo: 'all'
        }
    ]);


    const [currentPage, setCurrentPage] = useState(0);
    useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

    const [vars, setVars] = useState([]);
    const [externals, setExternals] = useState({
        flags: []
    });

    const [parent, setParent] = useState('');

    const getFormPages = () => {
        return FormService.getFormPages(location.state.form_id).then(res => {
            const _pages = []
            const _contents = [];
            res.data.map(item => {
                item["page"]["page_id"] = item.page_id;
                _pages.push(item["page"])
                item.content.map(content => {
                    content["page_id"] = item.page_id;
                    _contents.push(content);
                });
            });

            // Load is not an undoable edit: use the raw setter so history stays
            // empty and the first real edit seeds its baseline from this state.
            setComponentsRaw(_contents);
            setPages(_pages);

            return _pages;
        });
    }

    useEffect(() => {
        if (location.state != null && location.state != undefined) {
            info();
            FormService.getFormDetail(location.state.form_id).then(res => {
                console.log('Res ->', res);
                if ("creation_index" in res.externals) {
                    setCreationIndex(res.externals.creation_index);
                } else {
                    setCreationIndex(-1);
                }
                setName(res.name);
                setVars(res.vars);
                setExternals(res.externals);
                setParent(res.parent);
                setDbId(res.form_id);
                document.getElementById('formId').textContent = res.form_id;
                let _pages = getFormPages();
                _pages.then((e) => {
                    setLayout(whichLayoutForPages(e));
                });
            });
        }

    }, []);


    // Captures width and height of FormView, and updates components
    const dimRef = useRef(null);

    let components_render = [];
    for (let k = 0; k < components.length; ++k) {
        if (components[k].page === currentPage) {
            if (components[k].type === 'primitive/Label') {
                components_render.push(
                    <Label id={k} comps={components} setter={setComponents}
                        vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                        sel={selectedEl} setSel={setSelectedEl}
                        hov={hoveredEl} setHov={setHoveredEl}
                        vars={vars} setVars={setVars}
                        contextMenu={contextMenu} setContextMenu={setContextMenu}
                        magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                        magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/Box') {
                components_render.push(
                    <Box id={k} comps={components} setter={setComponents}
                        vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                        sel={selectedEl} setSel={setSelectedEl}
                        hov={hoveredEl} setHov={setHoveredEl}
                        vars={vars} setVars={setVars}
                        contextMenu={contextMenu} setContextMenu={setContextMenu}
                        magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                        magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/Input') {
                components_render.push(<Input id={k} comps={components} setter={setComponents}
                    vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                    sel={selectedEl} setSel={setSelectedEl}
                    hov={hoveredEl} setHov={setHoveredEl}
                    vars={vars} setVars={setVars}
                    contextMenu={contextMenu} setContextMenu={setContextMenu}
                    magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                    magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/Button') {
                components_render.push(<Button id={k} comps={components} setter={setComponents}
                    vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                    sel={selectedEl} setSel={setSelectedEl}
                    hov={hoveredEl} setHov={setHoveredEl}
                    vars={vars} setVars={setVars}
                    contextMenu={contextMenu} setContextMenu={setContextMenu}
                    magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                    magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/Radio') {
                components_render.push(<Radio id={k} comps={components} setter={setComponents}
                    vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                    sel={selectedEl} setSel={setSelectedEl}
                    hov={hoveredEl} setHov={setHoveredEl}
                    vars={vars} setVars={setVars}
                    contextMenu={contextMenu} setContextMenu={setContextMenu}
                    magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                    magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/Check') {
                components_render.push(<Check id={k} comps={components} setter={setComponents}
                    vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                    sel={selectedEl} setSel={setSelectedEl}
                    hov={hoveredEl} setHov={setHoveredEl}
                    vars={vars} setVars={setVars}
                    contextMenu={contextMenu} setContextMenu={setContextMenu}
                    magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                    magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/List') {
                components_render.push(<List id={k} comps={components} setter={setComponents}
                    vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                    sel={selectedEl} setSel={setSelectedEl}
                    hov={hoveredEl} setHov={setHoveredEl}
                    vars={vars} setVars={setVars}
                    contextMenu={contextMenu} setContextMenu={setContextMenu}
                    magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                    magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/Drop') {
                components_render.push(<Drop id={k} comps={components} setter={setComponents}
                    vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                    sel={selectedEl} setSel={setSelectedEl}
                    hov={hoveredEl} setHov={setHoveredEl}
                    vars={vars} setVars={setVars}
                    contextMenu={contextMenu} setContextMenu={setContextMenu}
                    magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                    magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'primitive/Image') {
                components_render.push(
                    <Image id={k} comps={components} setter={setComponents}
                        vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                        sel={selectedEl} setSel={setSelectedEl}
                        hov={hoveredEl} setHov={setHoveredEl}
                        vars={vars} setVars={setVars}
                        contextMenu={contextMenu} setContextMenu={setContextMenu}
                        magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                        magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'intermediate/Signature') {
                components_render.push(<Signature id={k} comps={components} setter={setComponents}
                    vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                    sel={selectedEl} setSel={setSelectedEl}
                    hov={hoveredEl} setHov={setHoveredEl}
                    vars={vars} setVars={setVars}
                    contextMenu={contextMenu} setContextMenu={setContextMenu}
                    magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                    magneticGridPartitions={magneticGridPartitions} />
                );
            } else if (components[k].type === 'intermediate/InputTable') {
                components_render.push(
                    <InputTable id={k} comps={components} setter={setComponents}
                        vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                        sel={selectedEl} setSel={setSelectedEl}
                        hov={hoveredEl} setHov={setHoveredEl}
                        vars={vars} setVars={setVars}
                        contextMenu={contextMenu} setContextMenu={setContextMenu}
                        magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                        magneticGridPartitions={magneticGridPartitions} />
                );
            }
            else if (components[k].type === 'intermediate/Snapshot') {
                components_render.push(
                    <Snapshot id={k} comps={components} setter={setComponents}
                        vw={0.75 * window.innerWidth} gridSize={size_of_grid}
                        sel={selectedEl} setSel={setSelectedEl}
                        hov={hoveredEl} setHov={setHoveredEl}
                        vars={vars} setVars={setVars}
                        contextMenu={contextMenu} setContextMenu={setContextMenu}
                        magneticGrid={magneticGrid} setMagneticGrid={setMagneticGrid}
                        magneticGridPartitions={magneticGridPartitions} />
                );
            }
        }

    }

    return (<>
        {contextHolder}
        <SelectionContext.Provider value={{ multiSel, setMultiSel, sel: selectedEl, magneticSnap, setSnapGuides }}>
        <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100vh',
            backgroundColor: '#fff',
            overflow: 'auto'
        }} id="wrapper" onScroll={(e) => {
            setContextMenu([false, -1, [0, 0]]);
        }}>

            <div className="create-form-container">

                {/* only for development */}
                {/* <h2>{creationIndex}</h2> */}

                <button style={{ display: 'none' }} ref={saveButton} onClick={saveForm}>Save</button>
                <ControlPanel comps={components} setComps={setComponents} sel={selectedEl}
                    setSel={setSelectedEl} multiSel={multiSel} setMultiSel={setMultiSel}
                    hov={hoveredEl} setHov={setHoveredEl}
                    pages={pages} setPages={setPages}
                    currentPage={currentPage} setCurrentPage={setCurrentPage} vars={vars} setVars={setVars}
                    externals={externals} setExternals={setExternals} parent={parent} setParent={setParent}
                    onFormSaveCallback={getFormPages}
                    name={name} setName={setName} dbId={dbId} setDbId={setDbId} pageId={pages[currentPage].page_id}
                    layout={layout} setLayout={setLayout} creationIndex={creationIndex} setCreationIndex={setCreationIndex} />

                {
                    (pages != null && pages.length > 0) ? (
                        <FormView reference={dimRef} vars={vars}
                            externals={externals}
                            parent={parent}
                            formName={name}
                            onFormSave={saveForm}
                            user={props?.user} formId={location?.state?.form_id} comps={components} setComps={setComponents}
                            content={components_render} position={{
                                x: '1.5vmax',
                                y: '87vh - ((100vh - (4vmax + 12vh) - 5vh) - 5vw + 3.8vw + 1pt + 1.5vmax)'
                            }} size={{
                                w: '100vw - 3vmax - 20vw - 4pt - 1.5vmax'
                            }} isEditable={true} pages={pages} setPages={setPages}
                            currentPage={currentPage} setCurrentPage={setCurrentPage}
                            setSelectedEl={setSelectedEl} setContextMenu={setContextMenu}
                            css={{
                                zIndex: '0'
                            }} magneticGrid={magneticGrid}
                            setMagneticGrid={setMagneticGrid}
                            magneticGridPartitions={magneticGridPartitions}
                            setMagneticGridPartitions={setMagneticGridPartitions}
                            layout={layout} setLayout={setLayout}
                            onUndo={undo} onRedo={redo} canUndo={canUndo} canRedo={canRedo}
                            setMultiSel={setMultiSel}
                            magneticSnap={magneticSnap} setMagneticSnap={setMagneticSnap} />
                    ) : null
                }
                {
                    contextMenu[0] ? <div className="create-form-ctx-menu" style={{
                        position: 'absolute',
                        top: `${document.getElementById("wrapper").scrollTop + contextMenu[2][1]}px`,
                        left: `${contextMenu[2][0]}px`,
                        zIndex: '100'
                    }}>
                        <div className='ctx-item' onClick={(e) => {
                            e.stopPropagation();
                            setContextMenu([false, -1, [0, 0]]);
                        }}>Close</div>
                        <div className='ctx-item' onClick={(e) => {
                            e.stopPropagation();
                            // Apply to the whole multi-selection when one is active,
                            // otherwise to the right-clicked element.
                            const ids = multiSel.length > 1 ? [...multiSel] : [contextMenu[1]];
                            // Duplicates are appended, so they occupy the indices
                            // starting at the current array length.
                            const startIndex = components.length;
                            let new_comps = [...components];
                            let ci = creationIndex;
                            ids.forEach((eid) => {
                                let object_to_push = JSON.parse(JSON.stringify(new_comps[eid]));
                                if ("creation_index" in object_to_push) {
                                    ci = ci + 1;
                                    object_to_push.creation_index = ci;
                                }
                                new_comps.push(object_to_push);
                            });
                            if (ci !== creationIndex) setCreationIndex(ci);
                            setComponents(new_comps);
                            // Immediately select the newly created element(s).
                            const newIndices = ids.map((_, i) => startIndex + i);
                            if (newIndices.length > 1) {
                                setMultiSel(newIndices);
                                setSelectedEl(-1);
                            } else {
                                setSelectedEl(newIndices[0]);
                                setMultiSel([]);
                            }
                            setContextMenu([false, -1, [0, 0]]);
                        }}>Duplicate</div>
                        <div className='ctx-item' onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEl(-1);
                            if (pages.length > 1) {
                                const ids = multiSel.length > 1 ? multiSel : [contextMenu[1]];
                                let new_comps = [...components];
                                ids.forEach((eid) => {
                                    if (new_comps[eid].page + 1 === pages.length) {
                                        new_comps[eid].page = 0;
                                    } else {
                                        new_comps[eid].page = new_comps[eid].page + 1;
                                    }
                                });
                                setComponents(new_comps);
                            }
                            setMultiSel([]);
                            setContextMenu([false, -1, [0, 0]]);
                        }}>To Right</div>
                        <div className='ctx-item' onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEl(-1);
                            if (pages.length > 1) {
                                const ids = multiSel.length > 1 ? multiSel : [contextMenu[1]];
                                let new_comps = [...components];
                                ids.forEach((eid) => {
                                    if (new_comps[eid].page === 0) {
                                        new_comps[eid].page = pages.length - 1;
                                    } else {
                                        new_comps[eid].page = new_comps[eid].page - 1;
                                    }
                                });
                                setComponents(new_comps);
                            }
                            setMultiSel([]);
                            setContextMenu([false, -1, [0, 0]]);
                        }}>To Left</div>
                        <div className='ctx-item' onClick={(e) => {
                            e.stopPropagation();
                            const ids = multiSel.length > 1 ? [...multiSel] : [contextMenu[1]];
                            // Toggle based on the right-clicked element's current state.
                            const willLock = !components[contextMenu[1]].locked;
                            let new_comps = [...components];
                            ids.forEach((eid) => { new_comps[eid] = { ...new_comps[eid], locked: willLock }; });
                            setComponents(new_comps);
                            setContextMenu([false, -1, [0, 0]]);
                        }}>{components[contextMenu[1]] && components[contextMenu[1]].locked ? 'Unlock' : 'Lock'}</div>
                        <div className='ctx-item ctx-danger' onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEl(-1);
                            // Splice highest index first so earlier indices stay valid.
                            const ids = (multiSel.length > 1 ? [...multiSel] : [contextMenu[1]]).sort((a, b) => b - a);
                            let new_comps = [...components];
                            ids.forEach((eid) => new_comps.splice(eid, 1));
                            setComponents(new_comps);
                            setMultiSel([]);
                            setContextMenu([false, -1, [0, 0]]);
                        }}>Delete</div>
                    </div> : <></>
                }

            </div>



        </div>
        {snapGuides.map((g, i) => (
            g.orientation === 'v'
                ? <div key={i} className="snap-guide snap-guide-v" style={{
                    left: g.x, top: g.top, height: g.bottom - g.top
                }} />
                : <div key={i} className="snap-guide snap-guide-h" style={{
                    top: g.y, left: g.left, width: g.right - g.left
                }} />
        ))}
        </SelectionContext.Provider>
        {saveStatus === 1 ? <Modal light width='15' height='21.5'>
            <p style={{
                fontWeight: '400',
                fontSize: '1.25vmax',
                marginTop: '3vh',
                padding: '1vmax'
            }}>Your form was successfully saved.</p>
            <a className='panel-button' onClick={(e) => {
                e.preventDefault();
                setSaveStatus(0);
            }}>Ok</a>
        </Modal> : <></>}

        {saveStatus === -1 ? <Modal light width='15' height='21.5'>
            <p style={{
                fontWeight: '400',
                fontSize: '1.25vmax',
                marginTop: '3vh',
                padding: '1vmax'
            }}>Your form could not be saved.</p>
            <a className='panel-button' onClick={(e) => {
                e.preventDefault();
                setSaveStatus(0);
            }}>Ok</a>
        </Modal> : <></>}
    </>);

}

function mapStateToProps(state) {
    const { user } = state.auth;
    return {
        user
    }
}

export default connect(mapStateToProps)(CreateForm);